import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import BusinessSignupV2 from '@/components/onboarding/v2/BusinessSignupV2';
import OnboardingTelemetry from '@/components/onboarding/v2/OnboardingTelemetry';
import { isPreviewDraft } from '@/utils/previewFirstDraft';
import { loadV2Draft } from '@/utils/v2OnboardingDraft';

/**
 * v2 onboarding page shell.
 * - DB-backed completion redirect (business_profiles + business_services + business_hours)
 * - Does NOT use hasCompletedOnboarding() — that function treats google_calendar as required
 * - Blocks render while completion check runs to prevent flash of onboarding UI for
 *   already-launched users navigating back via browser history.
 */
export default function MagicOnboardingPageV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const previewSaved = useMemo(
    () => searchParams.get('preview_saved') === '1' || isPreviewDraft(loadV2Draft()),
    [searchParams],
  );

  useEffect(() => {
    let cancelled = false;

    async function checkCompletion() {
      if (loading) return;

      if (searchParams.has('code') || searchParams.has('stripe_success') || searchParams.has('stripe_refresh')) {
        setChecking(false);
        return;
      }

      if (!user) {
        setChecking(false);
        return;
      }

      try {
        const { data: profileRows } = await supabase
          .from('business_profiles')
          .select('id, booking_url')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
        const profile = profileRows?.[0] ?? null;

        if (cancelled) return;
        if (!profile?.booking_url) { setChecking(false); return; }

        const { data: services } = await supabase
          .from('business_services')
          .select('id')
          .eq('business_id', profile.id)
          .limit(1);

        if (cancelled) return;
        if (!services?.length) { setChecking(false); return; }

        const { data: hours } = await supabase
          .from('business_hours')
          .select('id')
          .eq('business_id', profile.id)
          .eq('enabled', true)
          .limit(1);

        if (cancelled) return;
        if (hours?.length) {
          navigate('/dashboard', { replace: true });
        } else {
          setChecking(false);
        }
      } catch {
        if (!cancelled) setChecking(false);
      }
    }

    checkCompletion();
    return () => { cancelled = true; };
  }, [user, loading, navigate, searchParams]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5956F6]" />
      </div>
    );
  }

  return (
    <OnboardingTelemetry>
      {previewSaved && user && (
        <div
          role="status"
          className="mx-auto mt-4 max-w-[448px] rounded-2xl border border-[#5956F6]/20 bg-white/95 px-4 py-3 shadow-[0_12px_30px_rgba(39,35,104,.12)]"
        >
          <div className="text-sm font-extrabold text-[#17152F]">✓ Your page has been saved.</div>
          <div className="mt-1 text-xs leading-relaxed text-[#68657C]">
            Let’s finish the last few steps before you go live.
          </div>
        </div>
      )}
      <BusinessSignupV2 />
    </OnboardingTelemetry>
  );
}
